import { useEffect, useRef, useState } from "react";
import {
  customEvent,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  getCareVisitType,
  CareVisitType,
  OverlayModal,
} from "goi_common";
import MournerCountLeadModal from "@/components/common/MournerCountLeadModal";
import { buildCareFacilityLeadMessage } from "./careFacilityLeadMessage";

/** 제출한 사람에게는 어느 시설에서도 다시 띄우지 않는다. */
const CARE_FACILITY_LEAD_SUBMITTED_KEY = "goi_care_facility_lead_submitted";

/** 문서의 이 비율까지 내려온 순간 모달을 연다. */
const SCROLL_TRIGGER_RATIO = 0.4;

function hasSubmitted(): boolean {
  try {
    return !!localStorage.getItem(CARE_FACILITY_LEAD_SUBMITTED_KEY);
  } catch {
    return false;
  }
}

function markSubmitted() {
  try {
    localStorage.setItem(CARE_FACILITY_LEAD_SUBMITTED_KEY, "true");
  } catch {
    // localStorage 접근 불가 - 무시
  }
}

/**
 * 요양병원/호스피스를 본 적 있는 유저가 시설 상세(장례식장/장지/화장장)에 오면
 * 지역 장례 준비 리드 모달을 띄운다. A/B 아님, 전면 반영.
 *
 * 마운트 즉시 열지 않고 문서 40% 스크롤에서 여는 이유: 진입 즉시 띄우면 페이지를
 * 더 보려던 사람이 그냥 닫아버린다 (박진용님 피드백). 스크롤을 전혀 하지 않은
 * 유저에게는 뜨지 않는 것이 의도다.
 *
 * 장례식장 상세에서는 재방문 모달이 우선이므로 !isRevisit 일 때만 마운트한다.
 * 상세는 wiki/features/care-facility-lead-modal.md 참고.
 */
const CareFacilityLeadGate = ({
  path,
  facilityName,
  sido,
  sigungu,
}: {
  /** 제출 이벤트를 쏠 화면 */
  path: "parlor_detail" | "facility_detail" | "crematorium_detail";
  facilityName: string;
  /** 시설 데이터가 any 라 비어 있을 수 있다. 시도를 못 읽으면 모달을 띄우지 않는다 */
  sido?: number | string;
  sigungu?: number | string;
}) => {
  const [visitType, setVisitType] = useState<CareVisitType | null>(null);
  const openedRef = useRef(false);

  const sidoLabel = FUNERAL_SIDO.get(Number(sido)) || "";
  const region = [sidoLabel, FUNERAL_SIGUNGU.get(Number(sido))?.get(Number(sigungu))]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (hasSubmitted()) return;

    const visited = getCareVisitType();
    if (!visited) return;

    const handleScroll = () => {
      if (openedRef.current) return;

      const scrolled = window.scrollY + window.innerHeight;
      const trigger = document.documentElement.scrollHeight * SCROLL_TRIGGER_RATIO;
      if (scrolled < trigger) return;

      openedRef.current = true;
      window.removeEventListener("scroll", handleScroll);

      customEvent({ newGtm: { action: "uv_care_facility_lead_modal" } });
      setVisitType(visited);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 시도를 못 읽으면 제목이 "장례 정보를 찾고 계신가요?" 로 깨지므로 아예 띄우지 않는다.
  if (!visitType || !sidoLabel) return null;

  const close = () => setVisitType(null);

  return (
    <OverlayModal
      isOpen
      onClose={close}
      closeOnOverlayClick
      modalContentStyle={{
        minWidth: "340px",
        overflow: "scroll",
        borderRadius: "20px",
      }}
      overlayModalContent={
        <MournerCountLeadModal
          copy={{
            title: `${sidoLabel} 장례 정보를 찾고 계신가요?`,
            step1Description:
              "장례식장부터 화장장, 장지까지 예상 규모에 맞춰 필요한 준비와 비용을 안내해드릴게요.",
            step2Title: `${sidoLabel} 장례 미리 알아보기`,
            step2Description:
              "선택하신 조건에 맞춰 장례식장부터 화장장, 장지와 예상 비용까지 정리해드릴게요.\n정리가 완료되면 입력해주신 번호로 안내드려요.",
            submitLabel: "동의하고 장례 정보 안내받기",
          }}
          buildSlackMessage={(mournerCount) =>
            buildCareFacilityLeadMessage({
              visitType,
              facilityName,
              sidoLabel,
              region,
              mournerCount,
            })
          }
          onClose={close}
          onSubmitted={() => {
            customEvent({ newGtm: { path, trigger_and_target: "care_lead_mdl-submit" } });
            markSubmitted();
          }}
        />
      }
    />
  );
};

export default CareFacilityLeadGate;
